// Mirrored verbatim in TempMee/mobile-app — change both together.

import type {
  IOfficeMissingInfoSource,
  TMissingOfficeInfoField,
  TParkingCostType,
  TTimePerPatientKey,
} from '../types/OfficeMissingInfo';

/** A stored 'N/A' is treated as unanswered (GOTU-9592 plan OQ 1). */
export const NOT_ANSWERED = 'N/A';

export const isUnanswered = (value?: string | null): boolean => !value || value === NOT_ANSWERED;

const isEmptyList = (list?: string[] | null): boolean => !list?.length;

export const isParkingCostType = (value?: string | null): value is TParkingCostType =>
  value === 'hr' || value === 'day';

/** The time-per-patient field for a profession code, or `null` (Front Desk, unknown codes). */
/** The parking section is asked when any of its three fields is. */
export const isParkingAsked = (missingFields: TMissingOfficeInfoField[]): boolean =>
  missingFields.some(
    (field) => field === 'parkingType' || field === 'parkingCost' || field === 'parkingCostType',
  );

export const getTimePerPatientKey = (professionCode: string): TTimePerPatientKey | null => {
  switch (professionCode) {
    case 'RDH':
      return 'timePerPatientRDH';
    case 'DA':
      return 'timePerPatientDA';
    case 'DN':
      return 'timePerPatientDN';
    default:
      return null;
  }
};

/**
 * The office-profile fields the selected office still lacks for a shift of `professionCode`,
 * in the order the modal shows them. Pass a reduced code (EFDA → DA).
 */
export const getMissingOfficeInfoFields = (
  source: IOfficeMissingInfoSource,
  professionCode: string,
): TMissingOfficeInfoField[] => {
  const missing: TMissingOfficeInfoField[] = [];

  const timePerPatientKey = getTimePerPatientKey(professionCode);
  if (timePerPatientKey && isUnanswered(source[timePerPatientKey])) {
    missing.push('timePerPatient');
  }

  if (professionCode === 'RDH' && typeof source.isAssistedHygiene !== 'boolean') {
    missing.push('assistedHygiene');
  }

  const parking = source.parkingInformation;
  if (!parking?.parkingType) {
    missing.push('parkingType');
  }
  if (
    !parking ||
    typeof parking.isParkingFree !== 'boolean' ||
    (parking.isParkingFree === false && !((parking.cost ?? 0) > 0))
  ) {
    missing.push('parkingCost');
  }
  if (parking?.isParkingFree === false && !isParkingCostType(parking.costType)) {
    missing.push('parkingCostType');
  }

  if (isEmptyList(source.softwares)) {
    missing.push('softwares');
  }
  if (isEmptyList(source.insurances)) {
    missing.push('insurances');
  }
  if (isUnanswered(source.operatories)) {
    missing.push('operatories');
  }

  return missing;
};
